import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {Table} from 'primeng/table';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {forkJoin, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {Funcionalidad} from '../../../_models/funcionalidad';
import {PermisoSeccionControl} from '../../../_models/permiso-seccion-control';
import {Garantia, GarantiaConModalidades, GarantiaModalidad} from '../../../_models/garantia';
import {GarantiaHistorialCambio, GarantiaHistorialCambioPayload} from '../../../_models/garantia-historial-cambio';
import {ListaItem} from '../../../_models/lista-item';
import {GarantiaService} from '../../../_services/garantia.service';
import {ListaService} from '../../../_services/lista.service';
import {AutenticacionService} from '../../../_services/autenticacion.service';
import {SeguroService} from '../../../_services/seguro.service';

function noNullValidator(): ValidatorFn {
  return (control: AbstractControl) =>
    control.value === null || control.value === undefined ? {required: true} : null;
}

@Component({
  selector: 'app-admgar-listado',
  templateUrl: 'admgar-listado.component.html',
  styleUrls: ['admgar-listado.component.scss']
})
export class AdmgarListadoComponent implements OnInit {

  /** Referencia directa a la tabla para filtrado global y reset */
  @ViewChild('dt', {static: true}) tabla: Table;

  /** Listado original cargado desde el servicio */
  garantias: Garantia[] = [];

  /** Listado aplicado a la tabla */
  garantiasFiltradas: Garantia[] = [];

  /** Indica si la tabla está cargando datos */
  cargando = true;

  /** Definición de columnas de la tabla */
  columnas: any[];

  /** Texto del buscador general */
  busqueda = '';

  /** Modalidades de garantía cargadas desde ListasModalidadesGarantias */
  lisModalidades: ListaItem[] = [];

  /** Mapeo de código de lista al campo booleano en el modelo Garantia */
  private readonly MODALIDAD_CAMPO: Record<string, string> = {
    MICROCREDITO: 'indicadorMicrocredito',
    CONS: 'indicadorCons',
    COMERCIAL: 'indicadorComercial',
    VIVIENDA: 'indicadorVivienda'
  };

  /** Etiquetas legibles para cada campo del formulario que se audita en el historial de cambios */
  private readonly HISTORIAL_CAMPO_LABEL: Record<string, string> = {
    codigoSuperf: 'Código Superf.',
    codigoShivam: 'Código Shivam',
    idTipoGarantia: 'Tipo garantía',
    idOtraGarantia: 'Clase garantía',
    idBienDeDerecho: 'Bien / Derecho',
    pagarePersonaNatural: 'Pagaré PN',
    pagarePersonaJuridica: 'Pagaré PJ',
    idSeguro: 'Seguro sobre bien obligatorio',
    indicadorHabilitado: 'Habilitado',
    indicadorRequiereConstitucion: 'Requiere Constitución',
    indicadorRequiereReserva: 'Requiere Reserva',
  };

  /**
   * Devuelve el nombre del control de formulario correspondiente al código de modalidad
   */
  getCampoModalidad(codigo: string): string {
    return this.MODALIDAD_CAMPO[codigo]
      ?? `indicador${codigo.charAt(0).toUpperCase()}${codigo.slice(1).toLowerCase()}`;
  }

  /** Indica si hay al menos una modalidad configurada para mostrar la sección */
  get hayModalidades(): boolean {
    return this.lisModalidades.length > 0;
  }

  /** Opciones del dropdown Tipo garantía*/
  lisTiposGarantia: SelectItem[] = [];

  /** Opciones del dropdown Clase garantía */
  lisClasesGarantia: SelectItem[] = [];

  /** Opciones del dropdown Estado */
  lisEstados: SelectItem[] = [
    {label: 'Todos', value: 'TODOS'},
    {label: 'Habilitado', value: true},
    {label: 'Inhabilitado', value: false}
  ];

  /** Valor seleccionado en filtro Tipo garantía */
  filtroTipoGarantia: string = 'TODOS';

  /** Valor seleccionado en filtro Clase garantía */
  filtroClaseGarantia: string = 'TODOS';

  /** Valor seleccionado en filtro Estado */
  filtroEstado: boolean | string = 'TODOS';

  /** Controla la visibilidad del modal */
  mostrarModal = false;

  /** true = modo edición, false = modo creación */
  modoEdicion = false;

  /** Bloquea el botón Guardar mientras persiste */
  guardando = false;

  /** Indica si el formulario tiene una combinación duplicada de tipo/clase/bien */
  errorDuplicado = false;

  /** Activa mensajes de validación en el formulario */
  submitted = false;

  /** Índice de la pestaña activa en el modal */
  tabEditarActiva = 0;

  /** Índice de la pestaña anterior, para revertir si el usuario cancela el cambio */
  tabAnterior = 0;

  /**
   * Sección ADMLGI con sus controles
   */
  @Input() seccionListado: any = null;

  /** Resuelve el control de la sección listado por su código */
  private ctrl(codigo: string): PermisoSeccionControl | null {
    return this.seccionListado?.controles?.[codigo] ?? null;
  }

  /** Muestra el botón Agregar si el control AGREGA está habilitado y es visible */
  get mostrarBtnAgregar(): boolean {
    const c = this.ctrl('AGREGA');
    return c && !!c.editable;
  }

  /**
   * Muestra el icono de acciones en la fila si el rol tiene EDIGAR o CONHIS habilitado
   */
  get mostrarBtnAcciones(): boolean {
    const editar = this.ctrl('EDIGAR');
    const historial = this.ctrl('CONHIS');
    return !!editar?.editable || !!historial?.editable;
  }

  /** Muestra la pestaña Configuración del modal si el control EDIGAR está habilitado. */
  get mostrarTabConfiguracion(): boolean {
    const c = this.ctrl('EDIGAR');
    return c && !!c.editable;
  }

  /** Muestra la pestaña Historial del modal si el control CONHIS está habilitado. */
  get mostrarTabHistorial(): boolean {
    const c = this.ctrl('CONHIS');
    return c && !!c.editable;
  }

  /** Registros del historial de cambios de la garantía en edición */
  historialCambios: GarantiaHistorialCambio[] = [];

  /** Indica si la tabla de historial está cargando datos */
  cargandoHistorial = false;

  /** Garantía que se está editando actualmente */
  garantiaEditando: Garantia | null = null;

  /** Tipos de garantía para el formulario */
  lisTiposGarantiaForm: SelectItem[] = [];

  /** Clases de garantía para el formulario */
  lisClasesGarantiaForm: SelectItem[] = [];

  /** Bienes / Derechos para el formulario */
  lisBienesDerechos: SelectItem[] = [];

  /** Seguros habilitados */
  lisSeguros: SelectItem[] = [];

  /** Formulario reactivo para crear/editar garantía */
  formulario: FormGroup;

  constructor(
    private garantiaService: GarantiaService,
    private lisService: ListaService,
    private seguroService: SeguroService,
    private autServicio: AutenticacionService,
    private confirmacionServicio: ConfirmationService,
    private fb: FormBuilder
  ) {
  }

  ngOnInit(): void {
    this.cargarDatos();
  }

  /**
   * Construye el arreglo de columnas de la tabla incluyendo dinámicamente
   */
  private configurarColumnas(): void {
    const colsModalidades = this.lisModalidades.map(m => ({
      field: this.getCampoModalidad(m.codigo),
      header: m.nombre,
      sort: false
    }));

    this.columnas = [
      {field: 'tipoGarantia', header: 'ADMGAR.LBL_TIPO_GARANTIA', sort: false},
      {field: 'otraGarantia', header: 'ADMGAR.LBL_CLASE_GARANTIA', sort: false},
      {field: 'bienDeDerecho', header: 'ADMGAR.LBL_BIEN_DERECHO', sort: false},
      {field: 'codigoShivam', header: 'ADMGAR.LBL_CODIGO_SHIVAM', sort: false},
      {field: 'codigoSuperf', header: 'ADMGAR.LBL_CODIGO_SUPERF', sort: false},
      ...colsModalidades,
      {field: 'indicadorRequiereConstitucion', header: 'ADMGAR.LBL_REQ_CONSTITUCION', sort: false},
      {field: 'indicadorRequiereReserva', header: 'ADMGAR.LBL_REQ_RESERVA', sort: false},
      {field: 'auditoriaFecha', header: 'ADMGAR.LBL_ACTIVACION', sort: false},
      {field: 'pagarePersonaNatural', header: 'ADMGAR.LBL_PAGARE_PN_SHORT', sort: false},
      {field: 'pagarePersonaJuridica', header: 'ADMGAR.LBL_PAGARE_PJ_SHORT', sort: false},
      {field: 'seguro', header: 'ADMGAR.LBL_SEGURO_OBLIGATORIO', sort: false},
      {field: 'indicadorHabilitado', header: 'LBL_ESTADO', sort: false}
    ];
    if (this.mostrarBtnAgregar) {
      this.columnas.push({field: 'acciones', header: 'LBL_ACCIONES', sort: false},);
    }
  }

  /**
   * Carga listado de garantías y todas las listas necesarias para los filtros y el formulario.
   */
  private cargarDatos(): void {
    forkJoin([
      this.garantiaService.listadoConModalidades(),
      this.lisService.getMasterDetailsByOrden('ListasTiposGarantias'),
      this.lisService.getMasterDetailsByOrden('ListasOtrasGarantias'),
      this.lisService.getMasterDetailsByOrden('ListasBienesDeDerechos'),
      this.seguroService.listadoEnabled(),
      this.lisService.getMasterDetailsByOrden('ListasModalidadesGarantias')
    ]).subscribe({
      next: ([garantiasConModalidades, tiposGarantia, otrasGarantias, bienesDerechos, seguros, modalidades]) => {
        this.lisModalidades = modalidades ?? [];

        this.garantias = (garantiasConModalidades ?? []).map((item: GarantiaConModalidades) => {
          const g: any = {...item.garantia};
          this.lisModalidades.forEach(m => {
            const campo = this.getCampoModalidad(m.codigo);
            const registro = item.modalidades.find(gm => gm.idModalidadGarantia === m.idLista);
            g[campo] = registro ? registro.indicadorHabilitado : false;
          });
          return g as Garantia;
        });

        this.garantiasFiltradas = [...this.garantias];

        this.lisTiposGarantia = [
          {label: 'Todos', value: 'TODOS'},
          ...tiposGarantia.map(t => ({label: t.nombre, value: t.idLista}))
        ];
        this.lisClasesGarantia = [
          {label: 'Todos', value: 'TODOS'},
          ...otrasGarantias.map(o => ({label: o.nombre, value: o.idLista})),
        ];

        this.lisTiposGarantiaForm = tiposGarantia.map(t => ({label: t.nombre, value: t.idLista}));
        this.lisClasesGarantiaForm = [...otrasGarantias.map(o => ({label: o.nombre, value: o.idLista}))];
        this.lisBienesDerechos = bienesDerechos.map(b => ({label: b.nombre, value: b.idLista}));
        this.lisSeguros = seguros.map(s => ({label: s.nombre, value: s.idSeguro}));

        this.configurarColumnas();
        this.cargando = false;
      },
      error: (err) => {
        console.error('Error cargando datos:', err);
        this.cargando = false;
      }
    });
  }

  /**
   * Reconsulta solo el listado de garantías
   */
  private recargarListado(): void {
    this.garantiaService.listadoConModalidades().subscribe({
      next: (garantiasConModalidades) => {
        this.garantias = (garantiasConModalidades ?? []).map((item: GarantiaConModalidades) => {
          const g: any = {...item.garantia};
          this.lisModalidades.forEach(m => {
            const campo = this.getCampoModalidad(m.codigo);
            const registro = item.modalidades.find(gm => gm.idModalidadGarantia === m.idLista);
            g[campo] = registro ? registro.indicadorHabilitado : false;
          });
          return g as Garantia;
        });
        this.garantiasFiltradas = [...this.garantias];
        this.aplicarFiltros();
      },
      error: () => {}
    });
  }

  /**
   * Inicializa el formulario reactivo, precargando datos si se suministra
   */
  private inicializarFormulario(g?: Garantia): void {
    const controles: Record<string, any> = {
      codigoSuperf: [g?.codigoSuperf ?? '', [Validators.required, Validators.maxLength(6)]],
      codigoShivam: [g?.codigoShivam ?? '', [Validators.required, Validators.pattern(/^\d{1,3}$/)]],
      idTipoGarantia: [g?.idTipoGarantia ?? null, [Validators.required]],
      idOtraGarantia: [g?.idOtraGarantia ?? null, [Validators.required]],
      idBienDeDerecho: [g?.idBienDeDerecho ?? null, [Validators.required]],
      pagarePersonaNatural: [g?.pagarePersonaNatural ?? '', [Validators.required, Validators.maxLength(6)]],
      pagarePersonaJuridica: [g?.pagarePersonaJuridica ?? '', [Validators.maxLength(6)]],
      idSeguro: [g?.idSeguro ?? null],
      indicadorHabilitado: [g?.indicadorHabilitado ?? true],
      indicadorRequiereConstitucion: [g?.indicadorRequiereConstitucion ?? null, [noNullValidator()]],
      indicadorRequiereReserva: [g?.indicadorRequiereReserva ?? null, [noNullValidator()]]
    };

    this.lisModalidades.forEach(m => {
      const campo = this.getCampoModalidad(m.codigo);
      controles[campo] = [(g as any)?.[campo] ?? null, [noNullValidator()]];
    });

    this.formulario = this.fb.group(controles);
  }

  /** Acceso directo a los controles del formulario */
  get f() {
    return this.formulario?.controls;
  }

  /**
   * Bloquea teclas no numéricas en el input de Código shivam.
   */
  soloNumericos(event: KeyboardEvent): boolean {
    return /^[0-9]$/.test(event.key);
  }

  /**
   * Verifica si ya existe una garantía con la misma combinación de tipo, clase y bien.
   */
  private existeDuplicado(idTipo: any, idClase: any, idBien: any): boolean {
    return this.garantias.some(g => {
      const esElMismo = this.modoEdicion && g.idGarantia === this.garantiaEditando?.idGarantia;
      return !esElMismo
        && g.idTipoGarantia === idTipo
        && g.idOtraGarantia === idClase
        && g.idBienDeDerecho === idBien;
    });
  }

  /**
   * Abre el modal en modo creación con el formulario en blanco.
   */
  crear(): void {
    this.modoEdicion = false;
    this.garantiaEditando = null;
    this.submitted = false;
    this.errorDuplicado = false;
    this.tabEditarActiva = 0;
    this.tabAnterior = 0;
    this.inicializarFormulario();
    this.mostrarModal = true;
  }

  /**
   * Abre el modal en modo edición precargando los datos de la garantía seleccionada.
   */
  editar(row: Garantia): void {
    this.modoEdicion = true;
    this.garantiaEditando = row;
    this.submitted = false;
    this.errorDuplicado = false;
    this.tabEditarActiva = 0;
    this.tabAnterior = 0;
    this.historialCambios = [];
    this.inicializarFormulario(row);
    this.mostrarModal = true;
    if (this.mostrarTabHistorial) {
      this.cargarHistorialCambios(row.idGarantia);
    }
  }

  /**
   * Consulta el historial de cambios habilitados para la garantía indicada.
   * @param idGarantia Identificador UUID de la garantía
   */
  private cargarHistorialCambios(idGarantia: string): void {
    this.cargandoHistorial = true;
    this.garantiaService.listadoHistorialesCambios(idGarantia).subscribe({
      next: (registros) => {
        this.historialCambios = registros ?? [];
        this.cargandoHistorial = false;
      },
      error: () => {
        this.historialCambios = [];
        this.cargandoHistorial = false;
      }
    });
  }

  /**
   * Si el formulario tiene cambios sin guardar, solicita confirmación.
   */
  cerrarModal(): void {
    if (this.formulario?.dirty) {
      this.confirmacionServicio.confirm({
        key: 'unsaved',
        header: 'Cambios sin guardar',
        message: '¿Está seguro de salir sin guardar cambios?',
        accept: () => {
          this.mostrarModal = false;
          this.submitted = false;
        }
      });
    } else {
      this.mostrarModal = false;
      this.submitted = false;
    }
  }

  cancelar(): void {
    this.cerrarModal();
  }

  /**
   * Maneja el cambio de pestaña. Si se abandona la pestaña de Configuración con
   * cambios sin guardar, solicita confirmación antes de proceder.
   */
  onCambiarTab(event: { index: number }): void {
    const tabNueva = event.index;
    if (this.formulario?.dirty && tabNueva !== 0) {
      this.tabAnterior = tabNueva;
    } else {
      this.tabAnterior = tabNueva;
    }
  }

  /**
   * Valida el formulario y, si es correcto, almacena la garantía y sus modalidades
   */
  guardar(): void {
    this.submitted = true;
    this.errorDuplicado = false;
    if (!this.formulario.valid) {
      return;
    }
    const {idTipoGarantia, idOtraGarantia, idBienDeDerecho} = this.formulario.value;
    if (this.existeDuplicado(idTipoGarantia, idOtraGarantia, idBienDeDerecho)) {
      this.errorDuplicado = true;
      return;
    }

    this.guardando = true;
    const formValue = this.formulario.value;

    const camposModalidad = new Set(this.lisModalidades.map(m => this.getCampoModalidad(m.codigo)));
    const payloadGarantia = Object.fromEntries(
      Object.entries(formValue).filter(([k]) => !camposModalidad.has(k))
    ) as Partial<Garantia>;

    if (this.modoEdicion) {
      const actualizar: Garantia = {...this.garantiaEditando, ...payloadGarantia};
      camposModalidad.forEach(campo => delete (actualizar as any)[campo]);
      delete (actualizar as any).ip;

      const historialPayload = this.construirHistorialCambios(actualizar.idGarantia, formValue);
      this.garantiaService.actualizar(actualizar).pipe(
        switchMap(() =>
          this.garantiaService.agregarModalidadesBulk(this.construirBulkModalidades(actualizar.idGarantia))
        ),
        switchMap(() =>
          historialPayload.length > 0
            ? this.garantiaService.registrarHistorialCambiosBulk(historialPayload)
            : of(null)
        )
      ).subscribe({
        next: () => {
          if (!actualizar.indicadorHabilitado && this.garantiaEditando.indicadorHabilitado) this.garantiaService.envioNotification(actualizar.idGarantia).subscribe();
          this.formulario.markAsPristine();
          this.mostrarModal = false;
          this.guardando = false;
          this.recargarListado();
        },
        error: () => {
          this.guardando = false;
        }
      });
    } else {
      this.garantiaService.agregar(payloadGarantia as Garantia).pipe(
        switchMap(nueva =>
          this.garantiaService.agregarModalidadesBulk(this.construirBulkModalidades(nueva.idGarantia)).pipe(
            map(() => nueva)
          )
        ),
        switchMap(nueva => {
          const historialPayload = this.construirHistorialCreacion(nueva.idGarantia, formValue);
          return historialPayload.length > 0
            ? this.garantiaService.registrarHistorialCambiosBulk(historialPayload).pipe(map(() => nueva))
            : of(nueva);
        })
      ).subscribe({
        next: () => {
          this.formulario.markAsPristine();
          this.mostrarModal = false;
          this.guardando = false;
          this.recargarListado();
        },
        error: () => {
          this.guardando = false;
        }
      });
    }
  }

  /**
   * Compara los valores actuales del formulario con los datos originales de la garantía
   * y retorna el arreglo de cambios para registrar en el historial.
   */
  private construirHistorialCambios(
    idGarantia: string,
    formValue: any
  ): GarantiaHistorialCambioPayload[] {
    const idUsuario = this.autServicio.obtenerIdUsuarioLogueado();
    const cambios: GarantiaHistorialCambioPayload[] = [];

    const formatBooleano = (v: any): string => v === true ? 'Si' : v === false ? 'No' : '';
    const formatHabilitado = (v: any): string => v === true ? 'Habilitado' : v === false ? 'Deshabilitado' : '';

    const resolverLabel = (campo: string, valor: any): string => {
      if (campo === 'idSeguro' && (valor === null || valor === undefined)) { return '-'; }
      if (valor === null || valor === undefined) { return ''; }
      switch (campo) {
        case 'idTipoGarantia':  return this.lisTiposGarantiaForm.find(x => x.value === valor)?.label ?? String(valor);
        case 'idOtraGarantia':  return this.lisClasesGarantiaForm.find(x => x.value === valor)?.label ?? String(valor);
        case 'idBienDeDerecho': return this.lisBienesDerechos.find(x => x.value === valor)?.label ?? String(valor);
        case 'idSeguro':        return this.lisSeguros.find(x => x.value === valor)?.label ?? String(valor);
        default:                return String(valor);
      }
    };

    const CAMPOS_BOOLEANOS = new Set(['indicadorRequiereConstitucion', 'indicadorRequiereReserva']);
    const CAMPOS_ID       = new Set(['idTipoGarantia', 'idOtraGarantia', 'idBienDeDerecho', 'idSeguro']);
    const CAMPOS_HABILITADO = new Set(['indicadorHabilitado']);

    Object.keys(this.HISTORIAL_CAMPO_LABEL).forEach(campo => {
      const anterior = (this.garantiaEditando as any)[campo];
      const nuevo    = formValue[campo];
      if (anterior === nuevo) { return; }

      let valorAnterior: string;
      let valorNuevo: string;

      if (CAMPOS_BOOLEANOS.has(campo)) {
        valorAnterior = formatBooleano(anterior);
        valorNuevo    = formatBooleano(nuevo);
      } else if (CAMPOS_ID.has(campo)) {
        valorAnterior = resolverLabel(campo, anterior);
        valorNuevo    = resolverLabel(campo, nuevo);
      } else if (CAMPOS_HABILITADO.has(campo)) {
        valorAnterior = formatHabilitado(anterior);
        valorNuevo    = formatHabilitado(nuevo);
      } else {
        valorAnterior = anterior ?? '';
        valorNuevo    = nuevo    ?? '';
      }

      cambios.push({ idGarantia, campo: this.HISTORIAL_CAMPO_LABEL[campo], valorAnterior, valorNuevo, auditoriaUsuario: idUsuario });
    });

    this.lisModalidades.forEach(m => {
      const campo    = this.getCampoModalidad(m.codigo);
      const anterior = (this.garantiaEditando as any)[campo];
      const nuevo    = formValue[campo];
      if (anterior === nuevo) { return; }
      cambios.push({
        idGarantia,
        campo: m.nombre,
        valorAnterior: formatBooleano(anterior),
        valorNuevo:    formatBooleano(nuevo),
        auditoriaUsuario: idUsuario
      });
    });

    return cambios;
  }

  /**
   * Construye el historial de creación para un registro nuevo.
   * Todos los campos rastreados se registran con valorAnterior = '-'.
   */
  private construirHistorialCreacion(
    idGarantia: string,
    formValue: any
  ): GarantiaHistorialCambioPayload[] {
    const idUsuario = this.autServicio.obtenerIdUsuarioLogueado();
    const cambios: GarantiaHistorialCambioPayload[] = [];

    const formatBooleano = (v: any): string => v === true ? 'Si' : v === false ? 'No' : '';
    const formatHabilitado = (v: any): string => v === true ? 'Habilitado' : v === false ? 'Deshabilitado' : '';

    const resolverLabel = (campo: string, valor: any): string => {
      if (campo === 'idSeguro' && (valor === null || valor === undefined)) { return '-'; }
      if (valor === null || valor === undefined) { return ''; }
      switch (campo) {
        case 'idTipoGarantia':  return this.lisTiposGarantiaForm.find(x => x.value === valor)?.label ?? String(valor);
        case 'idOtraGarantia':  return this.lisClasesGarantiaForm.find(x => x.value === valor)?.label ?? String(valor);
        case 'idBienDeDerecho': return this.lisBienesDerechos.find(x => x.value === valor)?.label ?? String(valor);
        case 'idSeguro':        return this.lisSeguros.find(x => x.value === valor)?.label ?? String(valor);
        default:                return String(valor);
      }
    };

    const CAMPOS_BOOLEANOS  = new Set(['indicadorRequiereConstitucion', 'indicadorRequiereReserva']);
    const CAMPOS_ID         = new Set(['idTipoGarantia', 'idOtraGarantia', 'idBienDeDerecho', 'idSeguro']);
    const CAMPOS_HABILITADO = new Set(['indicadorHabilitado']);

    Object.keys(this.HISTORIAL_CAMPO_LABEL).forEach(campo => {
      const nuevo = formValue[campo];
      let valorNuevo: string;

      if (CAMPOS_BOOLEANOS.has(campo)) {
        valorNuevo = formatBooleano(nuevo);
      } else if (CAMPOS_ID.has(campo)) {
        valorNuevo = resolverLabel(campo, nuevo);
      } else if (CAMPOS_HABILITADO.has(campo)) {
        valorNuevo = formatHabilitado(nuevo);
      } else {
        valorNuevo = nuevo ?? '';
      }

      cambios.push({ idGarantia, campo: this.HISTORIAL_CAMPO_LABEL[campo], valorAnterior: '-', valorNuevo, auditoriaUsuario: idUsuario });
    });

    this.lisModalidades.forEach(m => {
      const campo = this.getCampoModalidad(m.codigo);
      const nuevo = formValue[campo];
      cambios.push({
        idGarantia,
        campo: m.nombre,
        valorAnterior: '-',
        valorNuevo:    formatBooleano(nuevo),
        auditoriaUsuario: idUsuario
      });
    });

    return cambios;
  }

  /**
   * Construye el payload bulk con todas las modalidades y su valor real (true o false).
   */
  private construirBulkModalidades(
    idGarantia: string
  ): Pick<GarantiaModalidad, 'idGarantia' | 'idModalidadGarantia' | 'indicadorHabilitado'>[] {
    return this.lisModalidades.map(m => ({
      idGarantia,
      idModalidadGarantia: m.idLista,
      indicadorHabilitado: !!this.formulario.get(this.getCampoModalidad(m.codigo))?.value
    }));
  }

  /**
   * Resuelve el valor a mostrar en la tabla del historial
   */
  resolverValorHistorial(row: GarantiaHistorialCambio, campo: 'valorAnterior' | 'valorNuevo'): string {
    const valor = row[campo];
    if (valor === null || valor === undefined || valor === '' || valor === 'No aplica') {
      return '-';
    }
    return valor;
  }

  /**
   * Filtra el listado de la tabla según los dropdowns seleccionados
   */
  aplicarFiltros(): void {
    this.garantiasFiltradas = this.garantias.filter(g => {
      const matchTipo = this.filtroTipoGarantia === 'TODOS' || g.idTipoGarantia === this.filtroTipoGarantia;
      const matchClase = this.filtroClaseGarantia === 'TODOS' || g.idOtraGarantia === this.filtroClaseGarantia;
      const matchEstado = this.filtroEstado === 'TODOS' || g.indicadorHabilitado === this.filtroEstado;
      return matchTipo && matchClase && matchEstado;
    });

    if (this.busqueda && this.busqueda.length >= 3) {
      this.tabla.filterGlobal(this.busqueda, 'contains');
    } else {
      this.tabla.filterGlobal('', 'contains');
    }
  }

  /**
   * Restablece todos los filtros y muestra el listado completo
   */
  limpiarFiltros(): void {
    this.filtroTipoGarantia = 'TODOS';
    this.filtroClaseGarantia = 'TODOS';
    this.filtroEstado = 'TODOS';
    this.busqueda = '';
    this.garantiasFiltradas = [...this.garantias];
    this.tabla.reset();
  }

  /**
   * Ejecuta el filtrado global por texto al superar los 3 caracteres
   */
  buscar(valor: string): void {
    this.busqueda = valor;
    if (valor.length === 0 || valor.length >= 3) {
      this.tabla.filterGlobal(valor, 'contains');
    }
  }

  /**
   * Confirma y ejecuta la eliminación de una garantía
   */
  /*confirmarEliminar(garantia: Garantia): void {
    this.confirmacionServicio.confirm({
      key: 'delete',
      header: 'Eliminar garantía',
      message: 'Este proceso eliminará la información y no se puede deshacer, ¿Cómo desea continuar?',
      icon: 'fa fa-exclamation-triangle',
      accept: () => {
        console.log('Eliminando:', garantia.idGarantia);
      }
    });
  }*/
}
